import { useEffect, useState } from "react"

import { ChevronRight } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { DateSection } from "@/components/filters/DateSection"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/collapsible"
import { cn } from "@/lib/utils"

interface CalendarSectionProps {
    section: string
    Icon?: LucideIcon
    onClear: number
    onSelection?: (selection: any) => void
}
export function CalendarSection({ section, Icon, onClear, onSelection }: Readonly<CalendarSectionProps>) {
    const [createdFrom, setCreatedFrom] = useState<string | undefined>(undefined)
    const [createdTo, setCreatedTo] = useState<string | undefined>(undefined)

    const [updatedFrom, setUpdatedFrom] = useState<string | undefined>(undefined)
    const [updatedTo, setUpdatedTo] = useState<string | undefined>(undefined)

    const handleOnCreatedFromChange = (date: Date | undefined) => {
        setCreatedFrom(date?.toISOString())
    }

    const handleOnCreatedToChange = (date: Date | undefined) => {
        setCreatedTo(date?.toISOString())
    }

    const handleOnUpdatedFromChange = (date: Date | undefined) => {
        setUpdatedFrom(date?.toISOString())
    }

    const handleOnUpdatedToChange = (date: Date | undefined) => {
        setUpdatedTo(date?.toISOString())
    }

    useEffect(() => {
        if (createdFrom || createdTo || updatedFrom || updatedTo) {
            onSelection?.({
                createdFrom,
                createdTo,
                updatedFrom,
                updatedTo,
            })
        } else {
            onSelection?.(undefined)
        }
    }, [createdFrom, createdTo, updatedFrom, updatedTo])

    return (
        <div className={cn("relative flex w-full min-w-0 flex-col p-2 border-b border-sidebar-border")}>
            <Collapsible defaultOpen={false} className="group/collapsible ">
                <CollapsibleTrigger className={cn("group/label w-full text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", "flex h-8 shrink-0 items-center rounded-md px-2 text-sm font-medium outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 hover:cursor-pointer mb-1")}>
                    {Icon && <Icon className="mr-2" />}
                    {section} <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="flex flex-col gap-4">
                        <DateSection section="Created At" onClear={onClear} from={createdFrom} to={createdTo} onFromValueChange={handleOnCreatedFromChange} onToValueChange={handleOnCreatedToChange} />
                        <DateSection section="Updated At" onClear={onClear} from={updatedFrom} to={updatedTo} onFromValueChange={handleOnUpdatedFromChange} onToValueChange={handleOnUpdatedToChange} />
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    )
}
